import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { MAX_ATTACHMENTS } from '@/constants/residents';
import { useToast } from '@/contexts/admin/ToastContext';
import { launchCamera, launchImageLibrary, ImagePickerResponse, Asset } from 'react-native-image-picker';
import { SUPPORTED_IMAGE_MIME_TYPES, isValidImageMimeType, SUPPORTED_FORMATS_DISPLAY } from '@/constants/admin/fileUpload';
import { MessageFileAttachment } from '@/store/api';

/**
 * Helper to revoke object URLs created by URL.createObjectURL to prevent memory leaks.
 * Only revokes URLs that start with 'blob:' (created by createObjectURL).
 */
const revokeObjectUrl = (uri: string) => {
  if (Platform.OS === 'web' && uri && uri.startsWith('blob:')) {
    try {
      (globalThis as any)?.URL?.revokeObjectURL?.(uri);
    } catch {
      // Ignore errors - URL may already be revoked
    }
  }
};

/**
 * Revoke object URLs for an array of upload files
 */
const revokeAllObjectUrls = (files: UploadFile[]) => {
  files.forEach((file) => revokeObjectUrl(file.uri));
};

export type UploadFile = {
  id: string; // Used for UI (removing by ID)
  name: string;
  uri: string;
  type: string;
  size?: number;
  file?: File; // Actual File object for web uploads
};

const assetsToUploadFiles = (assets: Asset[] | undefined): UploadFile[] => {
  if (!assets || assets.length === 0) return [];
  // For React Native, we use uri/type/name directly (no File object needed)
  // buildMessageFormData will use these fields for FormData
  return assets.map((asset, index) => ({
    id: `${Date.now()}_${index}`,
    name: asset.fileName || `image_${Date.now()}_${index}.jpg`,
    uri: asset.uri || '',
    type: asset.type || 'image/jpeg',
    size: asset.fileSize || undefined,
    // file property is only set for web uploads (actual File object)
  }));
};

export const useChatAttachments = () => {
  const [attachments, setAttachments] = useState<UploadFile[]>([]);
  const { showError } = useToast();
  
  // Track attachments for cleanup on unmount
  const attachmentsRef = useRef<UploadFile[]>([]);
  attachmentsRef.current = attachments;
  
  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      revokeAllObjectUrls(attachmentsRef.current);
    };
  }, []);

  const handleImageResponse = useCallback((response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) return;
    const newFiles = assetsToUploadFiles(response.assets);
    if (newFiles.length > 0) {
      setAttachments((prev) => {
        const spaceLeft = Math.max(0, MAX_ATTACHMENTS - prev.length);
        const next = spaceLeft > 0 ? [...prev, ...newFiles.slice(0, spaceLeft)] : prev;
        if (prev.length + newFiles.length > MAX_ATTACHMENTS) {
          showError(`You can attach up to ${MAX_ATTACHMENTS} files`);
        }
        return next;
      });
    }
  }, []);

  const addFromCamera = useCallback(() => {
    launchCamera({ mediaType: 'photo', quality: 0.8, maxWidth: 1024, maxHeight: 1024 }, handleImageResponse);
  }, [handleImageResponse]);

  const addFromLibrary = useCallback(() => {
    const selectionLimit = Math.max(1, MAX_ATTACHMENTS - attachments.length);
    launchImageLibrary({ mediaType: 'photo', quality: 0.8, maxWidth: 1024, maxHeight: 1024, selectionLimit }, handleImageResponse);
  }, [handleImageResponse, attachments.length]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const fileToRemove = prev.find((x) => x.id === id);
      if (fileToRemove) {
        revokeObjectUrl(fileToRemove.uri);
      }
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  const resetAttachments = useCallback(() => {
    setAttachments((prev) => {
      revokeAllObjectUrls(prev);
      return [];
    });
  }, []);

  // Convert attachments to API-ready format (memoized to avoid recalculation)
  const filesForApi: MessageFileAttachment[] | undefined = useMemo(() => {
    if (attachments.length === 0) return undefined;
    return attachments.map((att) => ({
      uri: att.uri,
      type: att.type,
      name: att.name,
      file: att.file,
    }));
  }, [attachments]);

  // Web-only file picker with specific image MIME type validation (matching admin events pattern)
  const addFromWebFilePicker = useCallback(() => {
    const win: any = (globalThis as any)?.window;
    const doc: any = win?.document;
    if (!doc) return;

    const input = doc.createElement('input');
    input.type = 'file';
    const remainingSlots = Math.max(1, MAX_ATTACHMENTS - attachments.length);
    input.multiple = remainingSlots > 1;
    // Use specific MIME types for images (JPEG, PNG, GIF, WebP)
    input.accept = SUPPORTED_IMAGE_MIME_TYPES.join(',');

    input.onchange = () => {
      const files = Array.from((input.files || []) as File[]);
      
      // Validate file types
      const validFiles = files.filter((file: File) => isValidImageMimeType(file.type));
      const invalidCount = files.length - validFiles.length;
      
      if (invalidCount > 0) {
        showError(`${invalidCount} file(s) not added. Please upload ${SUPPORTED_FORMATS_DISPLAY} images only.`);
      }
      
      if (validFiles.length === 0) {
        input.remove();
        return;
      }
      
      // Limit to remaining slots
      const filesToAdd = validFiles.slice(0, remainingSlots);
      
      const newFiles: UploadFile[] = filesToAdd.map((f: File, index: number) => ({
        id: `${Date.now()}_${index}`,
        name: f.name || `image_${Date.now()}_${index}.jpg`,
        uri: (win?.URL || URL).createObjectURL(f),
        type: f.type || 'image/jpeg',
        size: f.size || undefined,
        file: f, // Store actual File object for FormData uploads
      }));
      
      setAttachments((prev) => {
        const spaceLeft = Math.max(0, MAX_ATTACHMENTS - prev.length);
        const next = spaceLeft > 0 ? [...prev, ...newFiles.slice(0, spaceLeft)] : prev;
        if (prev.length + newFiles.length > MAX_ATTACHMENTS) {
          showError(`You can attach up to ${MAX_ATTACHMENTS} files`);
        }
        return next;
      });
      
      input.remove();
    };

    input.click();
  }, [attachments.length, showError]);

  return {
    attachments,
    filesForApi, // API-ready format (undefined if no attachments)
    setAttachments,
    addFromCamera,
    addFromLibrary,
    addFromWebFilePicker,
    removeAttachment,
    resetAttachments,
    handleImageResponse,
  } as const;
};

export default useChatAttachments;


